import { AxiosRequestConfig } from "axios";
import { Packet } from "../Interfaces/commonDtos";
import {
  IAdjustmentConfig,
  IAdjustmentTablesSearchParam,
  ICustomAdjustmentConfig,
  IDecrementalBaseTableReport,
  IDecrementalOptionTableReport,
  IAdjustment2SearchParam,
  IDeltaGammaTableReport,
  IIncrementalBaseTableReport,
  IIncrementalOptionTableReport,
  IAdjustment2Config,
  IAdjustment2Reference,
  IAdjustment2ManualGroupReference,
} from "../Interfaces/deltaHedgeAdjustment";
import { DropdownItem } from "../Interfaces/dropdownInterfaces";
import { Report } from "../Interfaces/TableInterfaces";
import {
  getRequest,
  getTable,
  postRequest,
  putRequest,
} from "./requestTemplates";

export const getAdjustmentConfig = (): Promise<IAdjustmentConfig> =>
  getRequest("/v1/deltaHedgeAdjustment/GetConfig");

export const setAdjustmentConfig = (
  config: IAdjustmentConfig
): Promise<Packet<boolean>> =>
  putRequest("/v1/deltaHedgeAdjustment/setConfig", config);

export const getAdjustment2Config = (): Promise<IAdjustment2Config> =>
  getRequest("/v1/deltaHedgeAdjustment/GetSetting2");

export const setAdjustment2Config = (
  config: IAdjustment2Config
): Promise<Packet<boolean>> =>
  postRequest("/v1/deltaHedgeAdjustment/SetSetting2", config);

export const getAdjustment2Reference = (
  groupId: number | string
): Promise<IAdjustment2Reference> =>
  getRequest(`/v1/deltaHedgeAdjustment/GetRefSetting?groupId=${groupId}`);

export const getManualGroupRefrenceSetting = (
  groupId: string
): Promise<IAdjustment2ManualGroupReference> =>
  getRequest(
    `/v1/deltaHedgeAdjustment/getManualGroupRefrenceSetting?groupId=${groupId}`
  );

export const setAdjustment2Reference = (
  config: IAdjustment2Reference,
  groupId: number | string
): Promise<Packet<boolean>> =>
  postRequest(
    `/v1/deltaHedgeAdjustment/SetRefSetting?groupId=${groupId}`,
    config
  );

export const getCustomAdjustmentConfig = (): Promise<ICustomAdjustmentConfig> =>
  getRequest("/v1/deltaHedgeAdjustment/GetCustomConfig");

export const setCustomAdjustmentConfig = (
  config: ICustomAdjustmentConfig
): Promise<Packet<boolean>> =>
  putRequest("/v1/deltaHedgeAdjustment/setCustomConfig", config);

export const getDecrementalOptionTable = (
  searchParam: IAdjustmentTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string
): Promise<Report<IDecrementalOptionTableReport>> =>
  getTable<IAdjustmentTablesSearchParam, IDecrementalOptionTableReport>(
    "/v1/deltaHedgeAdjustment/decrementalOptionTable",
    searchParam,
    offset,
    pageSize,
    sortingType
  );

export const getCustomDecrementalOptionTable = (
  searchParam: IAdjustmentTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<IDecrementalOptionTableReport>> =>
  getTable<IAdjustmentTablesSearchParam, IDecrementalOptionTableReport>(
    "/v1/deltaHedgeAdjustment/customDecrementalOptionTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getDecrementalBaseTable = (
  searchParam: IAdjustmentTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string
): Promise<Report<IDecrementalBaseTableReport>> =>
  getTable<IAdjustmentTablesSearchParam, IDecrementalBaseTableReport>(
    "/v1/deltaHedgeAdjustment/decrementalBaseTable",
    searchParam,
    offset,
    pageSize,
    sortingType
  );

export const getIncrementalOptionTable = (
  searchParam: IAdjustmentTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string
): Promise<Report<IIncrementalOptionTableReport>> =>
  getTable<IAdjustmentTablesSearchParam, IIncrementalOptionTableReport>(
    "/v1/deltaHedgeAdjustment/incrementalOptionTable",
    searchParam,
    offset,
    pageSize,
    sortingType
  );

export const getCustomIncrementalOptionTable = (
  searchParam: IAdjustmentTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<IIncrementalOptionTableReport>> =>
  getTable<IAdjustmentTablesSearchParam, IIncrementalOptionTableReport>(
    "/v1/deltaHedgeAdjustment/customIncrementalOptionTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getIncrementalBaseTable = (
  searchParam: IAdjustmentTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string
): Promise<Report<IIncrementalBaseTableReport>> =>
  getTable<IAdjustmentTablesSearchParam, IIncrementalBaseTableReport>(
    "/v1/deltaHedgeAdjustment/incrementalBaseTable",
    searchParam,
    offset,
    pageSize,
    sortingType
  );

export const getDeltaGammaTable = (
  searchParam: IAdjustment2SearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<IDeltaGammaTableReport>> =>
  getTable<IAdjustment2SearchParam, IDeltaGammaTableReport>(
    "/v1/deltaHedgeAdjustment/DeltaGamma",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getReferenceTable = (
  searchParam: IAdjustment2SearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<any>> =>
  getTable<IAdjustment2SearchParam, any>(
    "/v1/deltaHedgeAdjustment/Refrence",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getCalls = (groupId: number): Promise<DropdownItem[]> =>
  getRequest(`/v1/deltaHedgeAdjustment/getCalls?groupId=${groupId}`);

export const getPuts = (groupId: number): Promise<DropdownItem[]> =>
  getRequest(`/v1/deltaHedgeAdjustment/getPuts?groupId=${groupId}`);
